import type { ProductSelectorTabType } from 'components/shared/ProductAndClientSelector';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { ProductVariation, SelectedProductVariation } from 'models/productsReferential';

import type { WorkspaceType } from '../model';
import type { ProductSelectorState, WorkspacesStateMap, WorkspaceWithProductSelector } from './workspaces.model';

export type WorskpaceDashboardActions =
  | WorkspaceShowDashboard
  | WorkspaceHideDashboard
  | WorkspaceProductVariationSelected
  | WorkspaceClientSelected
  | WorkspaceSaveToLocalStorage
  | WorkspaceLoadFromLocalStorage
  | WorkspaceRecentProductVariationReceived
  | workspaceRecentProductVariationFetchFailed
  | WorkspaceProductSelectorTabSelected
  | WorkspaceProductSelectorFilterSelected
  | EssentialWorkspaceShowTabsPanelChanged;

export function workspaceShowDashboard(workspace: WorkspaceType) {
  return {
    type: 'WORKSPACE_SHOW_DASHBOARD' as const,
    workspace,
  };
}
export type WorkspaceShowDashboard = ReturnType<typeof workspaceShowDashboard>;

export function workspaceHideDashboard(workspace: WorkspaceType) {
  return {
    type: 'WORKSPACE_HIDE_DASHBOARD' as const,
    workspace,
  };
}
export type WorkspaceHideDashboard = ReturnType<typeof workspaceHideDashboard>;

export function workspaceProductVariationSelected(
  workspace: WorkspaceType,
  selectedProductVariation: Partial<SelectedProductVariation>,
) {
  return {
    type: 'WORKSPACE_PRODUCT_VARIATION_SELECTED' as const,
    workspace,
    selectedProductVariation,
  };
}
export type WorkspaceProductVariationSelected = ReturnType<typeof workspaceProductVariationSelected>;

export function workspaceClientSelected(workspace: WorkspaceType, client: Nullable<ISalesCounterpart>) {
  return {
    type: 'WORKSPACE_CLIENT_SELECTED' as const,
    workspace,
    client,
  };
}
export type WorkspaceClientSelected = ReturnType<typeof workspaceClientSelected>;

export function workspaceProductSelectorTabSelected(workspace: WorkspaceType, tabType: ProductSelectorTabType) {
  return {
    type: 'WORKSPACE_PRODUCT_SELECTOR_TAB_SELECTED' as const,
    workspace,
    tabType,
  };
}
export type WorkspaceProductSelectorTabSelected = ReturnType<typeof workspaceProductSelectorTabSelected>;

export function workspaceRecentProductVariationReceived(recentProductsByWorkspace: {
  [Workspace in WorkspaceWithProductSelector]?: readonly ProductVariation[];
}) {
  return {
    type: 'WORKSPACE_RECENT_PRODUCT_VARIATION_RECEIVED' as const,
    recentProductsByWorkspace,
  };
}
export type WorkspaceRecentProductVariationReceived = ReturnType<typeof workspaceRecentProductVariationReceived>;

export function workspaceRecentProductVariationFetchFailed(workspace: WorkspaceType, error: Error) {
  return {
    type: 'WORKSPACE_RECENT_PRODUCT_VARIATION_FETCH_FAILED' as const,
    workspace,
    error,
  };
}
export type workspaceRecentProductVariationFetchFailed = ReturnType<typeof workspaceRecentProductVariationFetchFailed>;

export function workspaceSaveToLocalStorage(workspace: WorkspaceType, keyValuePair: Partial<ProductSelectorState>) {
  return {
    type: 'WORKSPACE_SAVE_TO_LOCAL_STORAGE' as const,
    workspace,
    keyValuePair,
  };
}
export type WorkspaceSaveToLocalStorage = ReturnType<typeof workspaceSaveToLocalStorage>;

export function workspaceLoadFromLocalStorage(payload: DeepPartial<WorkspacesStateMap>) {
  return {
    type: 'WORKSPACE_LOAD_FROM_LOCAL_STORAGE' as const,
    payload,
  };
}
export type WorkspaceLoadFromLocalStorage = ReturnType<typeof workspaceLoadFromLocalStorage>;

export function workspaceProductSelectorFilterSelected(workspace: WorkspaceType, assetType: string) {
  return {
    type: 'WORKSPACE_PRODUCT_SELECTOR_FILTER_SELECTED' as const,
    workspace,
    assetType,
  };
}
export type WorkspaceProductSelectorFilterSelected = ReturnType<typeof workspaceProductSelectorFilterSelected>;

export function essentialWorkspaceShowTabsPanelChanged() {
  return {
    type: 'ESSENTIAL_WORKSPACE_SHOW_TABS_PANEL_CHANGED' as const,
  };
}
export type EssentialWorkspaceShowTabsPanelChanged = ReturnType<typeof essentialWorkspaceShowTabsPanelChanged>;
